import { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'universal-cookie';

import { handleError } from '@/services/handleError';

const VERSION_COOKIE_KEY = 'app_version';
const YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

const cookies = new Cookies(null, {
  path: '/',
  maxAge: YEAR_IN_SECONDS,
  sameSite: 'lax'
});

const getCookieVersion = (): string | null => {
  const version = cookies.get(VERSION_COOKIE_KEY);
  return version ? decodeURIComponent(version) : null;
};

const setCookieVersion = (version: string): void => {
  cookies.set(VERSION_COOKIE_KEY, encodeURIComponent(version));
};

const fetchVersion = async (): Promise<string | null> => {
  const res = await fetch(`/version.json?ts=${Date.now()}`, {
    cache: 'no-store'
  });

  if (!res.ok) return null;

  const data = await res.json();

  return typeof data?.version === 'string' ? data.version : null;
};

const useVersionCheck = (intervalSec = 60) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const currentVersion = useRef<string | null>(null);

  const handleNewVersionUpdate = useCallback(() => {
    if (!newVersion) return;

    setCookieVersion(newVersion);
    currentVersion.current = newVersion;
    setUpdateAvailable(false);
    setNewVersion(null);
  }, [newVersion]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const check = async () => {
      try {
        const version = await fetchVersion();

        if (version === null) return;

        if (currentVersion.current === null) {
          const cookieVersion = getCookieVersion();

          if (cookieVersion === null) {
            currentVersion.current = version;
            setCookieVersion(version);
            return;
          }

          currentVersion.current = cookieVersion;
        }

        if (version === currentVersion.current) {
          setUpdateAvailable(false);
          setNewVersion(null);
          return;
        }

        setUpdateAvailable(true);
        setNewVersion(version);
      } catch (error) {
        handleError(error, {
          notify: false
        });
      }
    };

    const startPolling = () => {
      if (intervalId !== null) return;

      check();
      intervalId = setInterval(check, intervalSec * 1000);
    };

    const stopPolling = () => {
      if (intervalId === null) return;

      clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }

      startPolling();
    };

    if (!document.hidden) startPolling();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalSec]);

  return {
    updateAvailable,
    handleNewVersionUpdate
  };
};

export default useVersionCheck;
